import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('mindforge_token'));
  const [premiumStatus, setPremiumStatus] = useState(null);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      // Fetch premium status
      fetchPremiumStatus();
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('mindforge_token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchPremiumStatus = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPremiumStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch premium status:', error);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('mindforge_token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name, category) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name, category });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('mindforge_token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('mindforge_token');
    setToken(null);
    setUser(null);
    setPremiumStatus(null);
  };

  const updateUser = async (data) => {
    const response = await axios.put(`${API}/auth/me`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setUser(response.data);
    return response.data;
  };

  const getAuthHeader = () => ({ Authorization: `Bearer ${token}` });
  
  const isFreeTierExhausted = () => {
    if (!premiumStatus) return false;
    return premiumStatus.free_tier_exhausted;
  };

  const refreshPremiumStatus = () => {
    if (token) fetchPremiumStatus();
  };

  return (
    <AuthContext.Provider value={{ 
      user, loading, token, login, register, logout, updateUser, getAuthHeader, API,
      premiumStatus, isFreeTierExhausted, refreshPremiumStatus 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
